import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const AUTH_URL = process.env.REACT_APP_AUTH_URL;
  const AUTH_REDIRECT_URL = `${window.location.origin}/`;  // Main app after login

  // Validate required environment variables
  if (!BACKEND_URL || !AUTH_URL) {
    console.error('[Auth] Missing required environment variables:', {
      BACKEND_URL: !!BACKEND_URL,
      AUTH_URL: !!AUTH_URL
    });
    throw new Error('Missing required environment variables. Please check your .env configuration.');
  }

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('[Auth] Initializing authentication check...');
        // Set a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          console.error('[Auth] Session check timeout - setting loading to false');
          setLoading(false);
          setAuthenticated(false);
        }, 10000); // 10 second timeout
        
        await checkSession();
        
        // Clear timeout if check completes
        clearTimeout(timeoutId);
      } catch (error) {
        console.error('[Auth] Init auth error:', error);
        setLoading(false);
        setAuthenticated(false);
      }
    };
    
    initAuth();
  }, []);

  // Check for session_id in URL fragment (after OAuth redirect)
  useEffect(() => {
    const processSessionId = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        console.log('[Auth] OAuth redirect detected, processing session_id...');
        // Extract session_id from URL fragment
        const match = hash.match(/session_id=([^&]+)/);
        if (match) {
          const sessionId = match[1];
          console.log('[Auth] Extracted session_id, exchanging for session token...');
          setLoading(true);
          
          try {
            // Exchange session_id for user data and session_token
            const response = await axios.post(
              `${BACKEND_URL}/api/auth/session-data`,
              {},
              {
                headers: {
                  'X-Session-ID': sessionId
                },
                withCredentials: true
              }
            );

            if (response.data.success) {
              console.log('[Auth] Session exchange successful, user:', response.data.user.email);
              console.log('[Auth] Cookie should now be set by backend');
              
              // Clean up URL fragment first
              window.history.replaceState(null, '', window.location.pathname);
              
              // Give mobile browsers time to process cookie (300ms)
              await new Promise(resolve => setTimeout(resolve, 300));
              
              // Now set auth state
              setUser(response.data.user);
              setAuthenticated(true);
              setLoading(false);
              
              console.log('[Auth] Authentication complete, app should now render');
            }
          } catch (error) {
            console.error('[Auth] Failed to exchange session_id:', error.response?.status, error.response?.data);
            setAuthenticated(false);
            setLoading(false);
          }
        }
      }
    };

    processSessionId();
  }, [BACKEND_URL]);

  const checkSession = async () => {
    try {
      console.log('[Auth] Checking session...');
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        withCredentials: true
      });

      if (response.data.authenticated) {
        console.log('[Auth] Session valid, user:', response.data.user.email);
        setUser(response.data.user);
        setAuthenticated(true);
      } else {
        console.log('[Auth] Session invalid');
        setAuthenticated(false);
      }
    } catch (error) {
      console.error('[Auth] Session check failed:', error.response?.status, error.response?.data);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    try {
      console.log('[Auth] Initiating login redirect...');
      console.log('[Auth] Redirect URL will be:', AUTH_REDIRECT_URL);
      
      // Redirect to Emergent Auth with our app URL as redirect
      const authUrl = `${AUTH_URL}/?redirect=${encodeURIComponent(AUTH_REDIRECT_URL)}`;
      console.log('[Auth] Redirecting to:', authUrl);
      
      // Set loading state before redirect
      setLoading(true);
      
      // Use window.location.href for redirect (works better on Safari)
      window.location.href = authUrl;
    } catch (error) {
      console.error('[Auth] Login redirect failed:', error);
      setLoading(false);
      alert('Failed to initiate login. Please try again.');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      setAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value = {
    user,
    authenticated,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
